import React, { useState, useEffect } from 'react'
import { Input, InputGroup } from 'components/input'
import { Divider } from 'components/divider'
import { Badge } from 'components/badge'
import { useLazyQuery, useMutation } from '@apollo/client'
import { ErrorDialog, SuccessDialog, ConfirmDialog } from 'components/custom/ResponseDialog'
import { t } from 'i18next'
import { GET_CHARTER_BOAT_CONFIGS } from 'graphql/charterBoats/queries'
import { DELETE_CHARTER_BOAT_CONFIG } from 'graphql/charterBoats/mutations'
import { useNavigate } from 'react-router-dom'
import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from 'components/pagination'
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from 'components/dropdown'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/table'
import { MagnifyingGlassIcon, EllipsisVerticalIcon } from '@heroicons/react/16/solid'
import { ArrowLeftIcon, PencilIcon, TrashIcon } from '@heroicons/react/20/solid'
import loadingImage from '../../../assets/images/boat-loading.gif'
import { BoatIcon } from 'components/icons'
import { getPageRange } from 'utils/paginationHelper'

export default function CharterBoatsConfiguration() {
    const navigate = useNavigate()
    
    // State management
    const [searchBoat, setSearchBoat] = useState('')
    const [searchFromDate, setSearchFromDate] = useState('')
    const [searchToDate, setSearchToDate] = useState('')
    const [configList, setConfigList] = useState([])
    const [configCount, setConfigCount] = useState(0)
    
    // Dialog states
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [confirmMessage, setConfirmMessage] = useState('')
    const [deleteConfigId, setDeleteConfigId] = useState(null)
    
    // Pagination
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const totalPages = Math.ceil(configCount / rowsPerPage)
    
    const [getConfigList, { loading: configListLoading, error: configListError }] = useLazyQuery(
        GET_CHARTER_BOAT_CONFIGS,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.getCharterBoatConfigs.success && data.getCharterBoatConfigs.code === '200') {
                    setConfigList(data.getCharterBoatConfigs.configurations)
                    setConfigCount(data.getCharterBoatConfigs.total_count)
                } else {
                    setErrorMessage(data.getCharterBoatConfigs.message || 'Failed to fetch configurations')
                    setShowErrDialog(true)
                }
            },
            onError: error => {
                setErrorMessage('Failed to fetch configurations')
                setShowErrDialog(true)
            }
        }
    )
    
    // Initial data load
    useEffect(() => {
        getConfigList({
            variables: {
                page: {
                    limit: rowsPerPage,
                    offset: page * rowsPerPage,
                },
                boat_id: searchBoat || null,
                from_date: searchFromDate || null,
                to_date: searchToDate || null
            },
        })
    }, [page, rowsPerPage])
    
    const handlePageChange = newPage => {
        setPage(newPage)
    }
    
    const onClickSearch = () => {
        setPage(0)
        getConfigList({
            variables: {
                page: {
                    limit: rowsPerPage,
                    offset: 0,
                },
                boat_id: searchBoat || null,
                from_date: searchFromDate || null,
                to_date: searchToDate || null
            },
        })
    }
    
    const onClickSearchClear = () => {
        setSearchBoat('')
        setSearchFromDate('')
        setSearchToDate('')
        setPage(0)
        getConfigList({
            variables: {
                page: {
                    limit: rowsPerPage,
                    offset: 0,
                },
                boat_id: null,
                from_date: null,
                to_date: null
            },
        })
    }
    
    const handleDialogClose = () => {
        setShowSucDialog(false)
        setShowErrDialog(false)
        setShowConfirmDialog(false)
        setConfirmMessage('')
        setSuccessMessage('')
        setErrorMessage('')
        setDeleteConfigId(null)
    }
    
    const handleDeleteClick = (configId) => {
        setDeleteConfigId(configId)
        setConfirmMessage('Are you sure you want to delete this configuration?')
        setShowConfirmDialog(true)
    }
    
    const [deleteConfig] = useMutation(DELETE_CHARTER_BOAT_CONFIG, {
        onCompleted: data => {
            if (data.deleteCharterBoatConfig.success && data.deleteCharterBoatConfig.code === '200') {
                setShowConfirmDialog(false)
                setSuccessMessage(t('Configuration deleted successfully'))
                setShowSucDialog(true)
                // Refresh list
                getConfigList({
                    variables: {
                        page: {
                            limit: rowsPerPage,
                            offset: page * rowsPerPage,
                        },
                        boat_id: searchBoat || null,
                        from_date: searchFromDate || null,
                        to_date: searchToDate || null
                    },
                })
            } else {
                setErrorMessage(data.deleteCharterBoatConfig.message || t('Failed to delete configuration'))
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to delete configuration'))
            setShowErrDialog(true)
        }
    })

    const handleConfirmPositive = () => {
        deleteConfig({
            variables: {
                id: deleteConfigId
            }
        })
    }
    
    const handleConfirmNegative = () => {
        setShowConfirmDialog(false)
        setDeleteConfigId(null)
    }
    
    const formatDate = (dateString) => {
        if (!dateString) return '-'
        const date = new Date(dateString)
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    }
    
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-BH', {
            style: 'currency',
            currency: 'BHD',
            minimumFractionDigits: 3,
            maximumFractionDigits: 3
        }).format(amount)
    }
    
    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>
            
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div>
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                        <BoatIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                    </div>
                                    <div>
                                        <span className="ms-3">{t('Charter Boats Configuration')}</span>
                                        <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                            {t('Manage charter boat pricing configurations')}
                                        </p>
                                    </div>
                                </h1>
                            </div>
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <button
                                    onClick={() => navigate('/charter-boats/configuration/add')}
                                    type="button"
                                    className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                >
                                    {t('Add New Configuration')}
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    {/* Search Filters */}
                    <div className="flex justify-between align-items-center mt-4">
                        <div>
                            <div className="mt-4 mb-4 flex flex-col sm:flex-row max-w-4xl gap-4">
                                <div className="flex-1">
                                    <label htmlFor="boat" className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Boat Name')}
                                    </label>
                                    <InputGroup>
                                        <Input
                                            name="boat"
                                            id="boat"
                                            value={searchBoat}
                                            onChange={e => setSearchBoat(e.target.value)}
                                            placeholder={t('Search by boat name')}
                                        />
                                    </InputGroup>
                                </div>
                                <div className="flex-1">
                                    <label htmlFor="fromDate" className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('From Date')}
                                    </label>
                                    <Input
                                        type="date"
                                        name="fromDate"
                                        id="fromDate"
                                        value={searchFromDate}
                                        onChange={e => setSearchFromDate(e.target.value)}
                                    />
                                </div>
                                <div className="flex-1">
                                    <label htmlFor="toDate" className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('To Date')}
                                    </label>
                                    <Input
                                        type="date"
                                        name="toDate"
                                        id="toDate"
                                        value={searchToDate}
                                        onChange={e => setSearchToDate(e.target.value)}
                                    />
                                </div>
                                <div className="flex-none grow space-x-2 justify-end place-content-end">
                                    <button
                                        type="button"
                                        onClick={onClickSearch}
                                        className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9] mt-6"
                                    >
                                        {t('Search')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onClickSearchClear}
                                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 mt-6"
                                    >
                                        {t('Clear')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <Divider />
            
            {/* Loading State */}
            {configListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            
            {/* Error State */}
            {configListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch configurations!')}</div>
                </div>
            )}
            
            {/* Empty State */}
            {!configListLoading && !configListError && configList?.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No configurations found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Get started by creating a new configuration')}</p>
                    <div className="mt-6">
                        <button
                            onClick={() => navigate('/charter-boats/configuration/add')}
                            type="button"
                            className="inline-flex items-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                        >
                            {t('Add New Configuration')}
                        </button>
                    </div>
                </div>
            )}
            
            {/* Table */}
            {!configListLoading && !configListError && configList?.length > 0 && (
                <>
                    <div className="table-custom flex justify-center items-center bg-white p-0">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('Boat Name')}</TableHeader>
                                    <TableHeader>{t('From Date')}</TableHeader>
                                    <TableHeader>{t('To Date')}</TableHeader>
                                    <TableHeader>{t('Base Price')}</TableHeader>
                                    <TableHeader>{t('Status')}</TableHeader>
                                    <TableHeader className="flex justify-end">{t('Actions')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {configList.map(config => (
                                    <TableRow
                                        key={config.id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() => navigate(`/charter-boats/configuration/edit/${config.id}`)}
                                        title={`Configuration #${config.id}`}
                                    >
                                        <TableCell>
                                            <div className="flex gap-2 items-center font-medium">
                                                {config.boat?.name?.en || '-'}
                                            </div>
                                        </TableCell>
                                        <TableCell>{formatDate(config.from_date)}</TableCell>
                                        <TableCell>{formatDate(config.to_date)}</TableCell>
                                        <TableCell className="font-semibold">{formatCurrency(config.base_price)}</TableCell>
                                        <TableCell>
                                            <Badge
                                                color={
                                                    config.status?.code === 'ACT'
                                                        ? 'emerald'
                                                        : config.status?.code === 'INA'
                                                        ? 'red'
                                                        : 'gray'
                                                }
                                            >
                                                {config.status?.description?.en}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    onClick={e => e.stopPropagation()}
                                                    className="cursor-pointer"
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/charter-boats/configuration/edit/${config.id}`)
                                                        }}
                                                    >
                                                        <PencilIcon />
                                                        <DropdownLabel>{t('Edit')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider />
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            handleDeleteClick(config.id)
                                                        }}
                                                    >
                                                        <TrashIcon className="text-red-500" />
                                                        <DropdownLabel className="text-red-500">{t('Delete')}</DropdownLabel>
                                                    </DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                    <Pagination className="py-6">
                        <PaginationPrevious
                            onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                            disabled={page === 0}
                        />
                        <PaginationList>
                            {getPageRange(page, totalPages).map(index => (
                                <PaginationPage
                                    key={index}
                                    current={index === page}
                                    onClick={() => handlePageChange(index)}
                                >
                                    {index + 1}
                                </PaginationPage>
                            ))}
                        </PaginationList>
                        <PaginationNext
                            onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                            disabled={page === totalPages - 1}
                        />
                    </Pagination>
                    <Divider />
                </>
            )}
            
            {/* Dialogs */}
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={successMessage} onClose={handleDialogClose} />
            )}
            {showConfirmDialog && (
                <ConfirmDialog
                    status={true}
                    title="Confirm Delete"
                    negativeActionText="Cancel"
                    positiveActionText="Delete"
                    message={confirmMessage}
                    onPositive={handleConfirmPositive}
                    onNegative={handleConfirmNegative}
                />
            )}
        </>
    )
}
